import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Estoque from './pages/Estoque';
import VeiculoDetalhe from './pages/VeiculoDetalhe';
import Clientes from './pages/Clientes';
import Vendas from './pages/Vendas';
import Relatorios from './pages/Relatorios';
import NovaVenda from './pages/NovaVenda';
import Financeiro from './pages/Financeiro';
import Fornecedores from './pages/Fornecedores';
import Usuarios from './pages/Usuarios';
import Perfil from './pages/Perfil';
import Cadastro from './pages/Cadastro';
import Landing from './pages/Landing';
import Master from './pages/Master';
import CRM from './pages/CRM';
import LeadsWhatsapp from './pages/LeadsWhatsapp';
import Remarketing from './pages/Remarketing';
import Comissao from './pages/Comissao';
import Consorcios from './pages/Consorcios';
import Suspenso from './pages/Suspenso';
import RecuperarSenha from './pages/RecuperarSenha';
import NovaSenha from './pages/NovaSenha';
import { MODULOS, isVisualizacao, podeVerModulo } from './utils/permissoes';

// Normaliza nomes de planos legados (sessões antigas) para os novos
const PLANO_NORM = { basico: 'starter', profissional: 'pro', completo: 'master' };
const normalizarPlano = (p) => PLANO_NORM[p] || p || 'starter';

// Plano/empresa continuam valendo para o usuário de visualização (ex.: Consórcio só Autop).
const ROTA_PLANOS = { '/crm': ['pro','master'], '/financeiro': ['pro','master'], '/fornecedores': ['pro','master'] };
const ROTA_EMPRESAS = { '/consorcios': [12] };
function moduloDisponivel(usuario, m) {
  if (!podeVerModulo(usuario, m.modulo)) return false;
  if (ROTA_PLANOS[m.path] && !ROTA_PLANOS[m.path].includes(normalizarPlano(usuario.plano))) return false;
  if (ROTA_EMPRESAS[m.path] && !ROTA_EMPRESAS[m.path].includes(usuario.empresa_id)) return false;
  return true;
}
// Destino quando a visualização cai numa tela não liberada: Dashboard, senão o 1º módulo liberado.
function destinoVisualizacao(usuario) {
  const m = MODULOS.find(x => moduloDisponivel(usuario, x));
  return m ? m.path : '/perfil';
}

function PrivateRoute({ children, adminOnly = false, planosPermitidos = null, empresasPermitidas = null, modulo = null }) {
  const { usuario, loading } = useAuth();
  if (loading) return <div style={{ color: '#1A3A5C', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: "'DM Sans',system-ui,sans-serif" }}>Carregando...</div>;
  if (!usuario) return <Navigate to="/login" />;
  // Visualização: vale só o que o admin liberou (rota sem módulo = nunca, exceto Meu perfil).
  if (isVisualizacao(usuario)) {
    const m = MODULOS.find(x => x.modulo === modulo);
    const liberado = m ? moduloDisponivel(usuario, m) : modulo === 'perfil';
    return liberado ? <Layout>{children}</Layout> : <Navigate to={destinoVisualizacao(usuario)} replace />;
  }
  if (adminOnly && usuario.role !== 'admin' && usuario.nivel !== 'admin' && usuario.nivel !== 'gerente') return <Navigate to="/dashboard" />;
  if (planosPermitidos && !planosPermitidos.includes(normalizarPlano(usuario.plano))) return <Navigate to="/dashboard" />;
  // Módulos exclusivos de um tenant (ex.: Consórcio da Autop) — bloqueia acesso por URL.
  if (empresasPermitidas && !empresasPermitidas.includes(usuario.empresa_id)) return <Navigate to="/dashboard" />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  // Se for rota master, renderizar painel master diretamente
  if (window.location.pathname === '/master') {
    return <Routes><Route path="/master" element={<Master />} /></Routes>;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Cadastro />} />
      <Route path="/suspenso" element={<Suspenso />} />
      <Route path="/recuperar-senha" element={<RecuperarSenha />} />
      <Route path="/nova-senha" element={<NovaSenha />} />
      <Route path="/dashboard" element={<PrivateRoute modulo="dashboard"><Dashboard /></PrivateRoute>} />
      <Route path="/estoque" element={<PrivateRoute modulo="estoque"><Estoque /></PrivateRoute>} />
      <Route path="/estoque/:id" element={<PrivateRoute modulo="estoque"><VeiculoDetalhe /></PrivateRoute>} />
      <Route path="/clientes" element={<PrivateRoute modulo="clientes"><Clientes /></PrivateRoute>} />
      <Route path="/vendas/nova" element={<PrivateRoute><NovaVenda /></PrivateRoute>} />
      <Route path="/vendas" element={<PrivateRoute modulo="vendas"><Vendas /></PrivateRoute>} />
      <Route path="/comissao" element={<PrivateRoute modulo="comissao"><Comissao /></PrivateRoute>} />
      {/* Consórcio: exclusivo da Autop (empresa 12), todos os níveis. Editar/cancelar é travado dentro da página (só admin). */}
      <Route path="/consorcios" element={<PrivateRoute modulo="consorcio" empresasPermitidas={[12]}><Consorcios /></PrivateRoute>} />
      <Route path="/perfil" element={<PrivateRoute modulo="perfil"><Perfil /></PrivateRoute>} />
      <Route path="/relatorios" element={<PrivateRoute modulo="relatorios" adminOnly><Relatorios /></PrivateRoute>} />
      <Route path="/financeiro" element={<PrivateRoute modulo="financeiro" adminOnly planosPermitidos={['pro','master']}><Financeiro /></PrivateRoute>} />
      <Route path="/fornecedores" element={<PrivateRoute modulo="fornecedores" adminOnly planosPermitidos={['pro','master']}><Fornecedores /></PrivateRoute>} />
      <Route path="/usuarios" element={<PrivateRoute adminOnly><Usuarios /></PrivateRoute>} />
      <Route path="/crm" element={<PrivateRoute modulo="crm" planosPermitidos={['pro','master']}><CRM /></PrivateRoute>} />
      <Route path="/leads-whatsapp" element={<PrivateRoute adminOnly><LeadsWhatsapp /></PrivateRoute>} />
      <Route path="/remarketing" element={<PrivateRoute adminOnly empresasPermitidas={[12]}><Remarketing /></PrivateRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
